import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import { styled } from '@mui/material/styles'
import { getFieldErrorMessages } from 'global/helpers/form'
import { type ReactElement } from 'react'
import { Controller, type FieldValues } from 'react-hook-form'
import type { KNFormRadioGroupProps } from './types'

const KNRadioGroup = styled(RadioGroup)(({ theme }) => ({
  variants: [
    {
      props: { variant: 'standard' },
      style: {
        '& .MuiRadio-root': {
          paddingTop: '4px',
          paddingBottom: '4px',
        },
      },
    },
    {
      props: { variant: 'outlined' },
      style: {
        border: `1px solid ${theme.palette.primary.light}`,
        borderRadius: theme.borders.borderRadius['md'],
        '& .MuiFormControlLabel-root': {
          margin: 0,
          '&:hover': {
            backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.hoverOpacity),
          },
          '&:has(.Mui-checked)': {
            backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.selectedOpacity),
            '&:hover': {
              backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity),
            },
          },
          '&.Mui-error .MuiRadio-root': {
            color: theme.palette.error.main,
          },
        },
        '& .MuiFormControlLabel-root + .MuiFormControlLabel-root': {
          borderTop: `1px solid ${theme.palette.primary.light}`,
        },
      },
    },
  ],
}))

const KNFormRadioGroup = <T extends FieldValues>({
  name,
  control,
  rules,
  label,
  options,
  sx,
  disabled,
  variant = 'standard',
}: KNFormRadioGroupProps<T>): ReactElement => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field: { onChange, value }, fieldState: { invalid, error }, formState }): ReactElement => (
      <FormControl fullWidth sx={sx} error={invalid} disabled={formState.isSubmitting}>
        {label && (
          <FormLabel sx={{ marginBottom: 0.5, color: disabled ? 'text.disabled' : 'primary.main' }}>{label}</FormLabel>
        )}
        <KNRadioGroup
          value={value}
          onChange={(event): void => onChange(event.target.value)}
          variant={variant}
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              disabled={disabled}
              control={<Radio />}
              label={option.label}
            />
          ))}
        </KNRadioGroup>
        {error?.message && <FormHelperText>{getFieldErrorMessages(error)}</FormHelperText>}
      </FormControl>
    )}
  />
)

export default KNFormRadioGroup
